/**
 * Form context for managing visits form state and operations
 *
 * Provides centralized state management for the visits form, including
 * draft management, form data, section progress, media uploads, and submission.
 */
 
/* eslint-disable no-console */

import { createContext, type ComponentChildren } from 'preact';
import { useContext, useState, useCallback, useRef, useEffect } from 'preact/hooks';
import { useRequest } from '@ox/blox';
import type { VisitFormDraft, MediaFile } from '@ox/ox-brokerage-client';
import type { ValidationErrors, EscalationPreview, FormData as FormDataOld } from '@ox/visits-ui/types';
import { visitFormsApi } from '@ox/visits-ui/clients/mtx';
import * as draftStorage from '@ox/visits-ui/services/draftStorage';
import validateSection from '@ox/visits-ui/utils/validation';
import { ACCESS_ISSUES, APPLIANCES, COMPARABLE_1, COMPARABLE_2, COMPARABLE_3, COMPARABLE_4, COMPARABLE_GATE, COMBINED_QC_PHOTO_UPLOAD, CONDITION, EXTERIOR_CONDITION, INSTRUCTIONS, QC_PHOTO_UPLOAD, PHOTO_UPLOAD, PROPERTY_ACCESS, QC_PROPERTY_ACCESS, STANDARD_EXTERIOR_CONDITION, SPARKLE_CLEAN, STANDARD_UTILITIES, UTILITIES, VALUATION, VIDEO_SUBMISSION, LIGHTWEIGHT_INTERIOR_CONDITION, STANDARD_INTERIOR_CONDITION } from '@ox/visits-ui/components/shared/Sections';
import { isImageFile } from '@ox/visits-ui/utils/imageCompression';
import { MOCK_MODE_ENABLED } from '@ox/visits-ui/config/mockMode';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Build a fully client-side visit draft for mock mode (no backend).
 *
 * The visit type defaults to "QC Standard" so the refreshed QC form renders,
 * but can be overridden with a `?visitType=` query param (e.g. "Pre-Walk",
 * "QC Lightweight") for testing other flows.
 */
const buildMockDraft = (propertyAddress: string): VisitFormDraft => {
  const params = new URLSearchParams(window.location.search);
  const visitType = params.get('visitType') || 'QC Standard';
  // `?currentSection=N` jumps straight to a section index for testing (0-based).
  const currentSection = Number(params.get('currentSection')) || 1;
  return {
    id: `mock-draft-${crypto.randomUUID()}`,
    propertyAddress,
    visitType,
    agentType: params.get('agentType') || undefined,
    invoicing: params.get('invoicing') || undefined,
    formData: {},
    currentSection,
    sectionsCompleted: [],
    media: [],
  } as unknown as VisitFormDraft;
};

const CHUNK_SIZE = Number(import.meta.env.CHUNK_SIZE) || 5 * 1024 * 1024; // Default to 5 MB
const MAX_CONCURRENT_UPLOADS = Number(import.meta.env.MAX_CONCURRENT_UPLOADS) || 3;
const MAX_RETRIES = Number(import.meta.env.MAX_RETRIES) || 3;

/**
 * Form context value interface
 *
 * @property formData - Form data object (all field values)
 * @property currentSection - Current section number (1-14)
 * @property sectionsCompleted - Array of completed section numbers
 * @property mediaFiles - Array of uploaded media files
 * @property isLoading - Whether a long operation is in progress (create/load/submit)
 * @property isSaving - Whether an auto-save is in progress
 * @property error - Error message (null if no error)
 * @property updateField - Update a single form field
 * @property updateMultipleFields - Update multiple form fields at once
 * @property completeSection - Mark a section as completed and trigger save
 * @property goToSection - Navigate to a specific section
 * @property uploadMedia - Upload photos or videos
 * @property deleteMedia - Delete an uploaded media file
 * @property getMediaByType - Get media files filtered by section and type
 * @property submitForm - Finalize and submit the form
 * @property loadDraft - Load an existing draft by ID
 * @property createDraft - Create a new draft with property address
 */
interface FormContextValue {
  sections: number[];
  draft: VisitFormDraft | null;
  formData: Record<string, string>;
  currentSection: number;
  sectionsCompleted: number[];
  mediaFiles: MediaFile[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  errors: ValidationErrors;
  viewMode: boolean;
  saveStatus: SaveStatus;
  lastSavedAt: Date | null;
  escalations: EscalationPreview[];
  showEscalationBanner: boolean;
  isMediaUploading: boolean;
  mediaUploadProgress: number;

  updateField: (field: string, value: unknown) => void;
  updateMultipleFields: (fields: Record<string, unknown>) => void;
  completeSection: (sectionNumber: number) => Promise<boolean>;
  goToSection: (sectionNumber: number) => void;
  uploadMedia: (files: File[], sectionNumber: number, mediaType: string) => Promise<void>;
  deleteMedia: (mediaId: string) => Promise<void>;
  getMediaByType: (sectionNumber: number, mediaType: string) => MediaFile[];
  submitForm: (sectionNumer: number) => Promise<boolean>;
  loadDraft: (draftId: string) => Promise<void>;
  createDraft: (propertyAddress: string, isRefresh: boolean) => Promise<VisitFormDraft>;
  setViewMode: () => void;
  dismissEscalationBanner: () => void;
  setError: (error: string | null) => void;
}

const getSectionOrder = (draft: VisitFormDraft): number[] => {
  const sections = [];

  const visitType = draft.visitType ?? null;
  const agentType = draft.agentType ?? null;
  const invoicing = draft.invoicing ?? null;

  if (visitType === 'Pre-Walk') {
    if (agentType === 'flex' || (agentType === 'preferred' && invoicing === 'showami')) {
      sections.push(INSTRUCTIONS, PROPERTY_ACCESS, UTILITIES, APPLIANCES, CONDITION, COMPARABLE_GATE, VALUATION, PHOTO_UPLOAD, VIDEO_SUBMISSION);
      if (draft.formData?.comp_method === 'manual') {
        sections.splice(sections.length - 3, 0, COMPARABLE_1, COMPARABLE_2, COMPARABLE_3, COMPARABLE_4);
      }
    } else {
      sections.push(INSTRUCTIONS, PROPERTY_ACCESS, UTILITIES, APPLIANCES, CONDITION, PHOTO_UPLOAD, VIDEO_SUBMISSION);
    }
  } else if (visitType === 'QC Lightweight') {
    sections.push(INSTRUCTIONS, QC_PROPERTY_ACCESS, UTILITIES, LIGHTWEIGHT_INTERIOR_CONDITION, EXTERIOR_CONDITION, QC_PHOTO_UPLOAD);
  } else if (visitType === 'QC Standard') {
    sections.push(INSTRUCTIONS, QC_PROPERTY_ACCESS, STANDARD_UTILITIES, STANDARD_INTERIOR_CONDITION, STANDARD_EXTERIOR_CONDITION, COMBINED_QC_PHOTO_UPLOAD, SPARKLE_CLEAN);
  }

  if (draft.formData?.were_able_to_access_property === 'No') {
    sections.splice(2, 0, ACCESS_ISSUES);
  }

  return sections;
};

const FormContext = createContext<FormContextValue | undefined>(undefined);

/**
 * Hook to access form context
 *
 * @returns Form context value with state and methods
 * @throws Error if used outside FormProvider
 *
 * @example
 * ```typescript
 * function Section2() {
 *   const { formData, updateField, completeSection } = useForm();
 *
 *   const handleNext = async () => {
 *     await completeSection(2);
 *     // Navigate to next section
 *   };
 *
 *   return (
 *     <input
 *       value={formData.field_name || ''}
 *       onChange={(e) => updateField('field_name', e.target.value)}
 *     />
 *   );
 * }
 * ```
 */
export function useForm() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within FormProvider');
  }
  return context;
}

interface FormProviderProps {
  children: ComponentChildren;
}

export function FormProvider({ children }: FormProviderProps) {
  const [draftId, setDraftId] = useState<string | null>(null);
  const [visitDraft, setVisitDraft] = useState<VisitFormDraft | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [currentSection, setCurrentSection] = useState(1);
  const [sectionsCompleted, setSectionsCompleted] = useState<number[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [viewMode, setViewMode] = useState<boolean>(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const autoSaveTimerRef = useRef<number | null>(null);
  const [sections, setSections] = useState<number[]>([]);
  const [escalations, setEscalations] = useState<EscalationPreview[]>([]);
  const [showEscalationBanner, setShowEscalationBanner] = useState(false);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [mediaUploadProgress, setMediaUploadProgress] = useState(0);

  // Set up useRequest hooks for each API operation
  const { request: requestCreateDraft } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.createDraftApiV1VisitFormsPost,
  });

  const { request: requestGetDraft } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.getDraftApiV1VisitFormsDraftIdGet,
  });

  const { request: requestUpdateDraft } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.updateDraftApiV1VisitFormsDraftIdPatch,
  });

  const { request: requestSubmitForm } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.submitFormApiV1VisitFormsDraftIdSubmitPost,
  });

  const { request: requestUploadMedia } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.uploadMediaApiV1VisitFormsDraftIdMediaPost,
  });

  const { request: requestDeleteMedia } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.deleteMediaApiV1VisitFormsDraftIdMediaMediaIdDelete,
  });

  const { request: requestInitChunksUpload } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.initChunksUploadApiV1VisitFormsChunkUploadInitPost,
  });

  const { request: requestCompleteChunksUpload } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.completeChunksUploadApiV1VisitFormsChunkUploadCompletePost,
  });

  const { request: requestAbortChunksUpload } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.abortChunksUploadApiV1VisitFormsChunkUploadAbortPost,
  });

  const updateField = useCallback(async (field: string, value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Save to IndexedDB immediately with the updated formData
      if (visitDraft) {
        draftStorage.saveDraft({
          ...visitDraft,
          formData: updated,
        }).catch((err) => {
          console.error('Failed to save to IndexedDB:', err);
        });
      }
      
      return updated;
    });
  }, [visitDraft]);

  const updateMultipleFields = useCallback(async (fields: Record<string, string>) => {
    setFormData((prev) => {
      const updated = { ...prev, ...fields };
      
      // Save to IndexedDB immediately with the updated formData
      if (visitDraft) {
        draftStorage.saveDraft({
          ...visitDraft,
          formData: updated,
        }).catch((err) => {
          console.error('Failed to save to IndexedDB:', err);
        });
      }
      
      return updated;
    });
  }, [visitDraft]);

  const uploadVideo = useCallback(
    async (file: File, draftId: string, sectionNumber: number, mediaType: string): Promise<MediaFile> => {
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);
      const initRes = await requestInitChunksUpload({
        chunksInitRequest: {
          draftId,
          sectionNumber,
          mediaType,
          filename: file.name,
          contentType: file.type,
          totalParts,
        }
      });
      if (!initRes.data) {
        throw new Error('Failed to initialize chunked upload');
      }

      const { uploadId, s3Path, presignedUrls } = initRes.data;

      const parts = [];
      const queue = [];
      let active = 0;
      const retryCount = new Map<number, number>();

      const processQueue = async () => {
        while (queue.length > 0 && active < MAX_CONCURRENT_UPLOADS) {
          active++;
          const task = queue.shift();
          try {
            const result = await task.uploadFn();
            parts.push({ etag: result.etag, part_number: task.partNumber });
            console.log(`✓ Chunk ${task.partNumber}/${totalParts} uploaded`);
            setMediaUploadProgress(Math.floor((parts.length * 100) / totalParts));
          } catch (error) {
            const currentRetries = retryCount.get(task.partNumber) || 0;
            if (currentRetries < MAX_RETRIES) {
              console.error(`Chunk ${task.partNumber} upload failed: ${error}. Retrying... (${currentRetries + 1}/${MAX_RETRIES})`);
              retryCount.set(task.partNumber, currentRetries + 1);
              queue.push(task); // Re-enqueue on failure
            } else {
              console.error(`Chunk ${task.partNumber} failed after ${MAX_RETRIES} retries`);
            }
          } finally {
            active--;
            // Continue processing queue when a slot frees up
            processQueue();
          }
        }
      };

      for (let i = 0; i < totalParts; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const partNumber = i + 1;
        const url = presignedUrls[i];

        const uploadFn = async () => {
          const res = await fetch(url, {
            method: 'PUT',
            body: chunk,
          });
          if (!res.ok) {
            throw new Error(`Failed to upload chunk ${partNumber}`);
          }
          return { etag: res.headers.get('ETag').replace(/"/g, '') };
        };

        queue.push({ uploadFn, partNumber });
      }

      await processQueue();

      while (active > 0 || queue.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (parts.length !== totalParts) {
        console.error('Some chunks failed');
        await requestAbortChunksUpload({ chunksAbortRequest: { s3Path, uploadId } });
        throw new Error('Chunk upload failed');
      }

      parts.sort((a, b) => a.part_number - b.part_number);
      const result = await requestCompleteChunksUpload({
        chunksCompleteRequest: {
          draftId: draftId,
          sectionNumber: sectionNumber,
          mediaType: mediaType,
          filename: file.name,
          contentType: file.type,
          fileSize: file.size,
          s3Path,
          uploadId,
          parts,
        }
      });
      if (!result.data) {
        throw new Error('Failed to complete chunked upload');
      }
      return result.data;
    },
    [requestAbortChunksUpload, requestCompleteChunksUpload, requestInitChunksUpload],
  );

  const saveToBackend = useCallback(
    async (overrides?: { sectionsCompleted?: number[] }): Promise<boolean> => {
      if (!draftId || !visitDraft) {
        return false;
      }

      const sectionsToSave = overrides?.sectionsCompleted ?? sectionsCompleted;

      // Sync to server
      try {
        setIsSaving(true);
        setSaveStatus('saving');

        await draftStorage.saveDraft({
          ...visitDraft,
          formData,
          currentSection,
          sectionsCompleted: sectionsToSave,
        });

        // Mock mode: no backend to sync to. Return *after* the local draft write
        // above so the "Saved" indicator is honest and a reload doesn't lose the
        // form — the whole point of the preview flow is walking a long
        // multi-section form.
        if (MOCK_MODE_ENABLED) {
          setLastSavedAt(new Date());
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 1500);
          return true;
        }

        const result = await requestUpdateDraft({
          draftId,
          updateVisitFormRequest: {
            formData: formData,
            currentSection: currentSection,
            sectionsCompleted: sectionsToSave,
          },
        });

        if (!result.data) {
          console.error('Auto-save failed:', result.error);
          setSaveStatus('error');
          return false;
        }

        setVisitDraft(result.data);
        setLastSavedAt(new Date());
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
        return true;
      } catch (err) {
        console.error('Auto-save failed:', err);
        setSaveStatus('error');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [draftId, visitDraft, formData, currentSection, sectionsCompleted, requestUpdateDraft],
  );

  const completeSection = useCallback(
    async (sectionNumber: number) => {
      if (sectionNumber === PROPERTY_ACCESS || sectionNumber === QC_PROPERTY_ACCESS) {
        if (formData.were_able_to_access_property === 'No') {
          setSections((prev) => {
            if (prev[currentSection + 1] === ACCESS_ISSUES) {
              return prev;
            }
            const updated = [...prev];
            updated.splice(currentSection + 1, 0, ACCESS_ISSUES);
            return updated;
          });
        } else {
          setSections((prev) => {
            if (prev[currentSection + 1] !== ACCESS_ISSUES) {
              return prev;
            }
            const updated = [...prev];
            updated.splice(currentSection + 1, 1);
            return updated;
          });
          setFormData((prev) => {
            if (!('access_issue_details' in prev)) {
              return prev;
            }
            const { access_issue_details: _, ...rest } = prev;
            return rest;
          });
        }
      } else if (sectionNumber === COMPARABLE_GATE) {
        if (formData.comp_method === 'manual') {
          setSections((prev) => {
            if (prev[currentSection + 1] === COMPARABLE_1) {
              return prev;
            }
            const updated = [...prev];
            updated.splice(currentSection + 1, 0, COMPARABLE_1, COMPARABLE_2, COMPARABLE_3, COMPARABLE_4);
            return updated;
          });
        } else {
          setSections((prev) => {
            if (prev[currentSection + 1] !== COMPARABLE_1) {
              return prev;
            }
            const updated = [...prev];
            updated.splice(currentSection + 1, 4);
            return updated;
          });
          setFormData((prev) => {
            const compKeys = Object.keys(prev).filter((k) => /^comp_\d+_/.test(k));
            if (compKeys.length === 0) {
              return prev;
            }
            const cleaned = { ...prev };
            for (const key of compKeys) {
              delete cleaned[key];
            }
            return cleaned;
          });
        }
      }

      const errors = validateSection(formData as FormDataOld, mediaFiles, sectionNumber, visitDraft?.parentVisitType);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return false;
      }

      const updatedSections = sectionsCompleted.includes(sectionNumber)
        ? sectionsCompleted
        : [...sectionsCompleted, sectionNumber];

      if (!sectionsCompleted.includes(sectionNumber)) {
        setSectionsCompleted(updatedSections);
      }

      // Save to IndexedDB immediately
      if (visitDraft) {
        try {
          await draftStorage.saveDraft({
            ...visitDraft,
            formData,
            currentSection,
            sectionsCompleted: updatedSections,
          });
        } catch (err) {
          console.error('Failed to save to IndexedDB:', err);
        }
      }

      await saveToBackend({ sectionsCompleted: updatedSections });
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sectionsCompleted, saveToBackend, formData, mediaFiles, visitDraft?.parentVisitType],
  );

  const goToSection = useCallback((sectionNumber: number) => {
    setCurrentSection(sectionNumber);
  }, []);

  const dismissEscalationBanner = useCallback(() => {
    setShowEscalationBanner(false);
  }, []);

  const uploadMedia = useCallback(
    async (files: File[], sectionNumber: number, mediaType: string) => {
      try {
        if (!draftId) {
          throw new Error('No draft');
        }

        setIsMediaUploading(true);
        setMediaUploadProgress(0);

        // Mock mode: create local MediaFile records with blob preview URLs.
        if (MOCK_MODE_ENABLED) {
          const mockFiles = files.map((file) => ({
            id: `mock-media-${crypto.randomUUID()}`,
            sectionNumber,
            mediaType,
            originalFilename: file.name,
            contentType: file.type,
            fileSize: file.size,
            signedUrl: URL.createObjectURL(file),
            createdAt: new Date().toISOString(),
          })) as unknown as MediaFile[];
          setMediaFiles((prev) => [...prev, ...mockFiles]);
          setMediaUploadProgress(100);
          return;
        }

        for (const [index, file] of files.entries()) {
          if (isImageFile(file)) {
            const result = await requestUploadMedia({
              draftId,
              files: [file],
              sectionNumber,
              mediaType,
            });
            if (!result.data || !result.data[0]) {
              throw new Error('Photo upload failed. Please try again.');
            }
            setMediaFiles((prev) => [...prev, result.data[0]]);
            setMediaUploadProgress(100 * (index + 1) / files.length);
          } else if (mediaType === 'walkthrough_video') {
            const video = await uploadVideo(file, draftId, sectionNumber, mediaType);
            setMediaFiles((prev) => [...prev, video]);
          }
        }
      } catch (err) {
        setError(err.message || 'Media upload failed. Please try again.');
      } finally {
        setIsMediaUploading(false);
        setMediaUploadProgress(0);
      }
    },
    // eslint-disable-next-line
    [draftId],
  );

  const deleteMedia = useCallback(
    async (mediaId: string) => {
      if (!draftId) {
        throw new Error('No draft ID');
      }

      // Mock mode: drop it from local state, no backend call.
      if (MOCK_MODE_ENABLED) {
        setMediaFiles((prev) => prev.filter((m) => m.id !== mediaId));
        return;
      }

      const result = await requestDeleteMedia({
        draftId,
        mediaId,
      });
      if (result.error) {
        setError('Failed to delete media. Please try again.');
      } else {
        setMediaFiles((prev) => prev.filter((m) => m.id !== mediaId));
      }
    },
    [draftId, requestDeleteMedia],
  );

  const getMediaByType = useCallback(
    (sectionNumber: number, mediaType: string) => {
      return mediaFiles.filter(
        (m) => m.sectionNumber === sectionNumber && m.mediaType === mediaType,
      );
    },
    [mediaFiles],
  );

  const submitForm = useCallback(async (sectionNumber: number) => {
    if (!draftId) {
      throw new Error('No draft ID');
    }

    if (formData.were_able_to_access_property === 'Yes') {
      const errors = validateSection(formData as FormDataOld, mediaFiles, sectionNumber, visitDraft?.parentVisitType);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        throw new Error('Validation errors');
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      // Mock mode: mark submitted client-side, skip the backend submit.
      if (MOCK_MODE_ENABLED) {
        setVisitDraft((prev) => ({ ...prev, submittedAt: new Date() } as VisitFormDraft));
        setViewMode(true);
        setIsLoading(false);
        return true;
      }

      // `requestSubmitForm` below sends only `{finalize: true}` — no form
      // data — so the final section's answers only reach the server via this
      // save. If it fails, finalizing anyway would complete the visit
      // without those answers while the agent still sees SuccessPage. Bail
      // before finalizing.
      const saved = await saveToBackend({ sectionsCompleted: visitDraft.sectionsCompleted });
      if (!saved) {
        setError('Could not save your answers. Check your connection and try again.');
        return false;
      }

      // Submit the form
      const result = await requestSubmitForm({
        draftId,
        submitVisitFormRequest: {
          finalize: true,
        },
      });

      // Only treat the submission as successful when the request actually
      // returned data. A failed/incomplete request (result.error, or the
      // aborted-request `{}` result from useRequest) must never stamp
      // submittedAt or advance the agent to the success screen. The raw
      // error text (e.g. "Response returned an error code") isn't
      // meaningful to a field agent, so log it and show the same
      // user-facing message deleteMedia already uses.
      if (!result.data) {
        console.error('Submit failed:', result.error);
        setError('Failed to submit form. Please try again.');
        return false;
      }

      setVisitDraft((prev) => ({ ...prev, submittedAt: new Date() } as VisitFormDraft));

      const response = result.data;
      // Handle escalations - check if escalations exist and convert to array if needed
      const escalationsData = response.escalations as unknown;
      if (escalationsData && Array.isArray(escalationsData) && escalationsData.length > 0) {
        setEscalations(escalationsData as EscalationPreview[]);
        setShowEscalationBanner(true);
      }

      setViewMode(true);

      // Clear local storage on successful submit
      await draftStorage.clearDraft(draftId);

      return true;
    } catch (err) {
      setError(err.message || 'Failed to submit form');
      throw err;
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, formData, mediaFiles, requestSubmitForm, requestUploadMedia, visitDraft?.parentVisitType]);

  const loadDraft = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      const result = await requestGetDraft({
        draftId: id,
      });

      if (result.data) {
        setSections(getSectionOrder(result.data));
        setMediaFiles(result.data.media);
        setDraftId(result.data.id);
        setVisitDraft(result.data);
        setFormData(result.data.formData);
        setCurrentSection(1);
        setSectionsCompleted(result.data.sectionsCompleted);
        setViewMode(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to load draft');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [requestGetDraft]);

  const createDraft = useCallback(async (propertyAddress: string, isRefresh: boolean) => {
    try {
      setIsLoading(true);

      // Mock mode: build the draft entirely client-side, no backend call.
      if (MOCK_MODE_ENABLED) {
        const mockDraft = buildMockDraft(propertyAddress);
        setSections(getSectionOrder(mockDraft));
        setDraftId(mockDraft.id);
        setVisitDraft(mockDraft);
        setFormData(mockDraft.formData as Record<string, string>);
        setCurrentSection(mockDraft.currentSection ?? 1);
        setSectionsCompleted(mockDraft.sectionsCompleted ?? []);
        setMediaFiles(mockDraft.media ?? []);
        setIsLoading(false);
        return mockDraft;
      }

      const result = await requestCreateDraft({
        createVisitFormRequest: {
          propertyAddress,
          isRefresh,
        },
      });

      let finalDraft = await draftStorage.getDraft(result.data.id);
      if (result.data) {
        if (!finalDraft) {
          finalDraft = result.data;
        } else {
          const localTime = new Date(finalDraft.updatedAt).getTime();
          const serverTime = new Date(result.data.updatedAt).getTime();
          finalDraft = {
            ...result.data,
            formData: finalDraft.formData,
            updatedAt: localTime > serverTime ? finalDraft.updatedAt : result.data.updatedAt,
          };
        }
        if (result.data.media) {
          setMediaFiles(result.data.media);
        }
      }

      if (!finalDraft.submittedAt) {
        await draftStorage.saveDraft(finalDraft);
      } else {
        setViewMode(true);
      }

      setSections(getSectionOrder(finalDraft));
      setDraftId(finalDraft.id);
      setVisitDraft(finalDraft);
      setFormData(finalDraft.formData);
      setCurrentSection(finalDraft.currentSection);
      setSectionsCompleted(finalDraft.sectionsCompleted);
      setIsLoading(false);

      return finalDraft;
    } catch (err) {
      setError(err.message || 'Failed to create draft');
      setIsLoading(false);
      throw err;
    }
  }, [requestCreateDraft]);

  const killAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current && viewMode === true) {
      clearInterval(autoSaveTimerRef.current);
    }
  }, [viewMode]);

  useEffect(() => {
    if (!draftId || viewMode) {
      return;
    }

    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = window.setInterval(saveToBackend, 30000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveToBackend]);

  useEffect(() => {
    killAutoSave();
  }, [killAutoSave, viewMode]);

  const value: FormContextValue = {
    sections,
    draft: visitDraft,
    formData,
    currentSection,
    sectionsCompleted,
    mediaFiles,
    isLoading,
    isSaving,
    error,
    errors: validationErrors,
    viewMode,
    saveStatus,
    lastSavedAt,
    escalations,
    showEscalationBanner,
    isMediaUploading,
    mediaUploadProgress,
    updateField,
    updateMultipleFields,
    completeSection,
    goToSection,
    uploadMedia,
    deleteMedia,
    getMediaByType,
    submitForm,
    loadDraft,
    createDraft,
    dismissEscalationBanner,
    setViewMode: () => {
      setViewMode(true);
      goToSection(0);
    },
    setError,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}
